import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Building2, Truck, FileText, LayoutDashboard, ArrowRight, Check, Sparkles,
} from "lucide-react";

const ICONS = { Building2, Truck, FileText, LayoutDashboard };

const money = (n) => "£" + Number(n || 0).toFixed(0);

export default function Landing() {
  const [c, setC] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/content/landing").then((r) => setC(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  if (!c) return <div className="min-h-screen bg-slate-50" />;
  const hero = c.hero || {};

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar transparent />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand/30 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[440px] w-[440px] rounded-full bg-brand-orange/20 blur-[120px]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-2 lg:items-center lg:px-8 lg:py-28">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-teal-200" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5" /> {hero.badge}
            </span>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {hero.title} <span className="text-teal-300">{hero.title_accent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg">{hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register"><Button className="h-12 rounded-full bg-brand px-7 text-base hover:bg-brand-hover" data-testid="hero-primary-btn">{hero.primary_btn} <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
              <a href="#how"><Button variant="ghost" className="h-12 rounded-full border border-white/15 px-6 text-base text-white hover:bg-white/10" data-testid="hero-secondary-btn">{hero.secondary_btn}</Button></a>
            </div>
            <p className="mt-4 text-xs text-slate-400">{hero.pill_text} · {hero.stat_label} <b className="text-slate-200">{hero.stat_value}</b></p>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img src={hero.image} alt="Commercial laundry" className="h-[380px] w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand">{c.features_subheading}</p>
        <h2 className="mt-3 max-w-2xl font-heading text-3xl font-bold text-slate-900 lg:text-4xl">{c.features_heading}</h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(c.features || []).map((f, i) => {
            const Icon = ICONS[f.icon] || Building2;
            return (
              <div key={i} className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg" data-testid={`feature-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-brand"><Icon className="h-5 w-5" strokeWidth={1.6} /></span>
                <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-slate-900 lg:text-4xl">{c.how_heading}</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {(c.how_steps || []).map((s, i) => (
              <div key={i} className="relative rounded-2xl border border-slate-200 bg-white p-7" data-testid={`how-step-${i}`}>
                <span className="font-heading text-5xl font-extrabold text-accent">{i + 1}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-5xl px-5 py-20 lg:px-8" data-testid="pricing">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-900 lg:text-4xl">Simple, transparent pricing</h2>
          <p className="mt-3 text-slate-500">Start free for 14 days. No credit card required.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {plans.map((p, i) => (
            <div key={p.id} className={`rounded-2xl border p-8 ${i === 1 ? "border-brand bg-slate-950 text-white" : "border-slate-200 bg-white"}`} data-testid={`plan-card-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                {i === 1 && <span className="rounded-full bg-brand px-3 py-1 text-xs font-semibold">Popular</span>}
              </div>
              <p className={`mt-1 text-sm ${i === 1 ? "text-slate-300" : "text-slate-500"}`}>{p.description}</p>
              <p className="mt-5 font-heading text-4xl font-extrabold">{money(p.price_monthly)}<span className="text-base font-medium text-slate-400">/mo</span></p>
              <p className="text-xs text-slate-400">or {money(p.price_yearly)}/year</p>
              <ul className="mt-6 space-y-2.5">
                {(p.features || []).map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${i === 1 ? "text-teal-300" : "text-brand"}`} /> {f}</li>
                ))}
              </ul>
              <Link to="/register" className="mt-7 block"><Button className={`w-full rounded-full ${i === 1 ? "bg-brand hover:bg-brand-hover" : "bg-slate-900 hover:bg-slate-800"}`} data-testid={`plan-cta-${p.name.toLowerCase()}`}>Start free trial</Button></Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand py-16 text-white">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="font-heading text-3xl font-bold lg:text-4xl">{c.cta?.title}</h2>
          <p className="mt-3 text-teal-50">{c.cta?.subtitle}</p>
          <div className="mt-7 flex justify-center gap-3">
            <Link to="/register"><Button className="h-12 rounded-full bg-white px-7 text-base text-brand hover:bg-slate-100" data-testid="cta-primary-btn">{c.cta?.primary_btn}</Button></Link>
            <Link to="/login"><Button variant="ghost" className="h-12 rounded-full border border-white/30 px-6 text-base text-white hover:bg-white/10" data-testid="cta-secondary-btn">{c.cta?.secondary_btn}</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-5 text-center text-sm text-slate-400 lg:px-8">{c.brand?.footer}</div>
      </footer>
    </div>
  );
}
